import { AbstractAddComponent } from '.././../../shared/abstracts/add/abstract-add/abstract-add.component';
import { Component, OnInit, Input } from '@angular/core';
import { IPatient, ICondition, ICondition_Evidence } from '@ahryman40k/ts-fhir-types/lib/R4';
import { FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Observable } from 'rxjs';
import { BNO } from '../models/bno.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { startWith, map, tap } from 'rxjs/operators';

@Component({
  selector: 'inc-condition-add',
  templateUrl: './condition-add.component.html',
  styleUrls: ['./condition-add.component.scss'],
})
export class ConditionAddComponent extends AbstractAddComponent<{ condition: ICondition, files: File[] }> implements OnInit {
  bnoList: BNO[] = [];
  filteredOptions: Observable<BNO[]>;
  editNote: any;
  evidence: ICondition_Evidence[];

  @Input() urlBno: string;
  @Input() patient: IPatient;
  @Input() categories: string[];
  @Input() isModal: boolean;
  @Input() user: any;
  @Input() type: string;

  files: File[] = [];

  constructor(
    private modalController: ModalController, private httpClient: HttpClient
  ) {
    super();
  }

  ngOnInit() {
    this.loadBNOs();
    this.filteredOptions = this.form.get('code.text').valueChanges
      .pipe(
        startWith(''),
        map(name => this._filter(name).slice(0, 100)),
        tap((name) => {
          this.form.get('code.coding').setValue([{
            system: 'https://www.who.int/',
            code: (name && name.length === 1) ? name[0].KOD10 : '',
            display: '',
          }]);
        })
      );
    this.form.controls.subject.setValue({ display: this.patient.name[0].text, reference: this.patient.id });
    if (this.user) {
      const obj = {
        reference: this.user.uid,
        display: this.user.displayName
      };
      this.form.get('note.0').setValue({
        authorReference: obj,
        text: '',
        time: new Date().toISOString()
      });
      this.form.get('recorder').setValue(obj);
    }
    if (this.inData) {
      this.editNote = this.form.controls.note.value;
      this.form.patchValue(this.inData.condition ? this.inData.condition : null);
      this.files = this.inData.files ? this.inData.files : [];
      this.evidence = this.inData.condition.evidence;
      setTimeout(() => {
        this.form.get('code.coding').patchValue([{
          code: this.inData.condition.code.coding[0].code ? this.inData.condition.code.coding[0].code : '',
        }]);
      }, 100);
    } else {
      if (this.type) {
        this.form.get('stage.0.type.text').setValue(this.type);
      }
    }
  }

  resetForm() {
    return new FormGroup({
      id: new FormControl(),
      onsetDateTime: new FormControl(new Date().toISOString()),
      category: new FormArray([
        new FormGroup({
          coding: new FormArray([
            new FormGroup({
              system: new FormControl('http://terminology.hl7.org/CodeSystem/condition-category'),
              code: new FormControl('problem-list-item'),
              display: new FormControl('Problem List Item')
            })
          ]),
          text: new FormControl(null, Validators.required)
        }),
      ]),
      code: new FormGroup({
        coding: new FormArray([
          new FormGroup({
            system: new FormControl('https://www.who.int/'),
            code: new FormControl(''),
            display: new FormControl('')
          })
        ]),
        text: new FormControl(null)
      }),
      note: new FormArray([
        new FormGroup({
          authorReference: new FormGroup({
            reference: new FormControl(''),
            display: new FormControl('')
          }),
          text: new FormControl(),
          time: new FormControl(new Date().toISOString())
        })
      ]),
      subject: new FormGroup({
        reference: new FormControl(this.patient ? this.patient.id : ''),
        display: new FormControl(this.patient ? this.patient.name[0].text : '')
      }),
      recorder: new FormGroup({
        reference: new FormControl(this.user ? this.user.uid : ''),
        display: new FormControl(this.user ? this.user.displayName : '')
      }),
      stage: new FormArray([
        new FormGroup({
          type: new FormGroup({
            text: new FormControl('')
          }),
          summary: new FormGroup({
            text: new FormControl('')
          })
        })
      ])
    });
  }

  close(data?: ICondition) {
    if (data) {
      data.onsetDateTime = new Date(data.onsetDateTime).toISOString();
      if (this.evidence) {
        data.evidence = this.evidence;
      }
      if (data.note.length > 1 && this.editNote) {
        data.note[1] = this.editNote[0];
      } else if (data.note.length === 1 && this.editNote) {
        data.note.push(this.editNote[0]);
      }
      if (data.code.text) { data.code.coding[0].display = data.code.text; }
      if (data.category.length > 0 && data.category[0].text) { data.stage[0].summary.text = data.category[0].text; }
    }
    if (this.isModal) {
      data ? this.modalController.dismiss({ condition: data, files: this.files }) : this.modalController.dismiss();
    } else {
      this.save({ condition: data, files: this.files });
    }
  }

  onSelectionChange(event) {
    if (event && event.value) {
      const control = this.form.get('code.text');
      if (event.value === 'disease') {
        control.setValidators(Validators.required);
      } else {
        control.setValue(null);
        control.clearValidators();
      }
      control.updateValueAndValidity();
    }
  }

  _filter(name: string): BNO[] {
    let filterValue = '';
    if (name) {
      filterValue = name.toLowerCase();
    }
    return this.bnoList.filter(bno => bno.NEV.toLowerCase().indexOf(filterValue) === 0);
  }

  loadBNOs() {
    if (this.urlBno) {
      this.readJson().subscribe((result) => {
        this.bnoList = result;
      });
    }
  }

  readJson(): Observable<BNO[]> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.httpClient.get<BNO[]>(this.urlBno, httpOptions);
  }
}
